import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import styles from './styles'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { aICreatorGetDocumentHeadersAction } from 'src/reducers/ai-creator/action'
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@material-ui/core'
import { CoreTextField } from '../core'
const flattenHeaders = (
  headers,
  level = 0,
  parentTitle = '',
  attachmentData = null,
) => {
  let flattened = []
  headers.forEach(header => {
    const indent = '\u00A0'.repeat(level * 4)
    const title = header.element_id
    flattened.push({
      ...header,
      level,
      displayTitle: indent + title,
      fullPath: parentTitle ? `${parentTitle} > ${title}` : title,
      attachment_id: attachmentData?.id,
      url: attachmentData?.url,
      fileName: attachmentData?.name,
    })
    if (header.children && header.children.length > 0) {
      flattened = flattened.concat(
        flattenHeaders(header.children, level + 1, title, attachmentData),
      )
    }
  })
  return flattened
}

const AddSubheaderModal = forwardRef(
  ({ outline, outlineId, classes, moduleIndex, onTitleSelect }, ref) => {
    const dispatch = useDispatch()
    const [title, setTitle] = useState('')
    const [documentHeaders, setDocumentHeaders] = useState([])
    const [flattenedHeaders, setFlattenedHeaders] = useState([])
    const [selectedTitle, setSelectedTitle] = useState(null)
    const [pageType, setPageType] = useState('info')
    const [showError, setShowError] = useState(false)
    const [showSelectionError, setShowSelectionError] = useState(false)

    useImperativeHandle(ref, () => ({
      setShowError: value => {
        setShowError(value)
      },
      resetState: () => {
        setTitle('')
        setSelectedTitle(null)
        setShowError(false)
        setShowSelectionError(false)
        setPageType('info')
      },
      validateSelection: () => {
        if (!selectedTitle) {
          setShowSelectionError(true)
          return false
        }
        return true
      },
    }))

    useEffect(() => {
      dispatch(
        aICreatorGetDocumentHeadersAction(outlineId, response => {
          if (response) {
            let allHeaders = []
            response.forEach((doc, index) => {
              if (doc?.attachment?.headers) {
                const flattened = flattenHeaders(
                  doc.attachment.headers,
                  0,
                  '',
                  doc.attachment,
                )
                allHeaders.push({
                  fileName: doc.attachment.name,
                  headers: flattened,
                })
              }
            })
            setFlattenedHeaders(allHeaders)
          }
        }),
      )
    }, [])

    useEffect(() => {
      // Focus the title field when modal opens
      const timer = setTimeout(() => {
        const titleInput = document.querySelector('input[type="text"]')
        if (titleInput) {
          titleInput.focus()
        }
      }, 100)
      return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
      // Reset error state when title changes to valid state
      if (title.trim()) {
        if (title.trim().length >= 2) {
          setShowError(false)
        }
      }
    }, [title])

    const handleTitleChange = e => {
      const newTitle = e.target.value
      setTitle(newTitle)
      // If we have a selected item, update the onTitleSelect with new title
      if (selectedTitle && onTitleSelect) {
        onTitleSelect({
          ...selectedTitle,
          text: newTitle.trim(),
          module_title: newTitle.trim(),
          content_type: pageType,
        })
      }
    }

    const handleTitleSelect = value => {
      setSelectedTitle(value)
      setShowSelectionError(false)
      if (value && onTitleSelect) {
        onTitleSelect({
          element_id: value.element_id,
          text: title.trim(),
          module_title: title.trim(),
          page_start: value.page_start,
          page_end: value.page_end,
          content_type: pageType,
          attachment_id: value.attachment_id,
          url: value.url,
          fileName: value.fileName,
          attachment: {
            id: value.attachment_id,
            name: value.fileName,
            page_start: value.page_start,
            page_end: value.page_end,
            url: value.url,
          },
        })
      }
    }

    const handlePageTypeChange = event => {
      const newPageType = event.target.value
      setPageType(newPageType)
      // Update parent when page type changes if we have a selection
      if (selectedTitle && onTitleSelect) {
        onTitleSelect({
          ...selectedTitle,
          text: title.trim(),
          module_title: title.trim(),
          content_type: newPageType,
        })
      }
    }

    return (
      <Box
        style={{
          minWidth: '600px',
          maxWidth: '800px',
          width: '720px',
        }}
      >
        <CoreTextField
          value={title}
          onChange={handleTitleChange}
          variant="outlined"
          placeholder="Enter page title (at least 2 characters)"
          label="What's the title of your page? "
          fullWidth
          autoFocus
          error={showError}
          helperText={
            showError && 'Please enter a title with at least 2 characters'
          }
          FormHelperTextProps={{
            className: showError
              ? classes.helperTextError
              : classes.helperTextNormal,
          }}
        />
        <Typography className={classes.titlePaper}>
          Select content from your source
        </Typography>
        <Box>
          <Paper
            variant="outlined"
            className={`${classes.scrollableContainer} ${
              showSelectionError ? 'error' : ''
            }`}
          >
            <List className={classes.listContainer}>
              {flattenedHeaders.map((pdfGroup, pdfIndex) => (
                <React.Fragment key={`pdf-${pdfIndex}`}>
                  {pdfIndex > 0 && <Divider className={classes.divider} />}
                  <ListItem className={classes.pdfListItem}>
                    <ListItemText
                      primary={pdfGroup.fileName}
                      primaryTypographyProps={{
                        className: classes.pdfFileName,
                      }}
                    />
                  </ListItem>
                  {pdfGroup.headers.map((item, index) => (
                    <React.Fragment key={`${item.element_id}-${index}`}>
                      <ListItem
                        button
                        selected={selectedTitle?.element_id === item.element_id}
                        onClick={() => handleTitleSelect(item)}
                      >
                        <ListItemIcon className={classes.listItemIcon}>
                          <Radio
                            checked={
                              selectedTitle?.element_id === item.element_id
                            }
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.element_id}
                          primaryTypographyProps={{
                            className: classes.contentListItem,
                            style: { paddingLeft: item.level * 20 + 8 },
                          }}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </List>
          </Paper>
          {showSelectionError && (
            <Typography className={classes.errorText}>
              Please select content from the list
            </Typography>
          )}
        </Box>

        <Box className={classes.pageTypeContainer}>
          <Typography className={classes.titlePaper}>
            Select page type
          </Typography>
          <RadioGroup row value={pageType} onChange={handlePageTypeChange}>
            <FormControlLabel
              value="info"
              control={<Radio color="primary" />}
              label="Info page"
            />
            <FormControlLabel
              value="quiz"
              control={<Radio color="primary" />}
              label="Quiz page"
            />
          </RadioGroup>
        </Box>
      </Box>
    )
  },
)

export default withStyles(styles)(AddSubheaderModal)
